import React, { useState } from 'react';
import { ref, set, get, update } from 'firebase/database';
import { db } from '../firebase';
import { FiUpload, FiDownload, FiPlus, FiCheckCircle, FiLoader, FiMonitor } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export default function AddDevice() {
  const [macAddress, setMacAddress] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [deviceGroup, setDeviceGroup] = useState('');
  const [excelFile, setExcelFile] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isProcessingExcel, setIsProcessingExcel] = useState(false);
  const [progress, setProgress] = useState(0);

  // Validate MAC address format (e.g., XX:XX:XX:XX:XX:XX)
  const validateMacAddress = (mac) => {
    const macRegex = /^([0-9A-Fa-f]{2}:){5}([0-9A-Fa-f]{2})$/;
    return macRegex.test(mac);
  };

  // Format input to MAC address (e.g., 001122334455 -> 00:11:22:33:44:55)
  const formatMacAddress = (input) => {
    // Remove any non-hexadecimal characters except colons
    let cleaned = input.replace(/[^0-9A-Fa-f]/g, '');
    // Limit to 12 characters (6 pairs of 2)
    cleaned = cleaned.slice(0, 12);
    // Insert colons after every 2 characters
    let formatted = '';
    for (let i = 0; i < cleaned.length; i++) {
      formatted += cleaned[i];
      if (i % 2 === 1 && i < cleaned.length - 1) {
        formatted += ':';
      }
    }
    return formatted;
  };

  // Handle MAC address input change
  const handleMacAddressChange = (e) => {
    const input = e.target.value;
    const formatted = formatMacAddress(input);
    setMacAddress(formatted);
  };

  // Check if a device already exists in Firebase
  const checkDeviceExists = async (macAddress) => {
    const deviceRef = ref(db, `mdm/devices/${macAddress}`);
    const snapshot = await get(deviceRef);
    return snapshot.exists();
  };

  // Add a single device to Firebase
  const addDevice = async (deviceData) => {
    const { macAddress, name, group } = deviceData;
    if (!validateMacAddress(macAddress)) {
      toast.error(`Invalid MAC address format: ${macAddress}. Expected format: XX:XX:XX:XX:XX:XX`);
      return false;
    }

    try {
      const exists = await checkDeviceExists(macAddress);
      if (exists) {
        toast.error(`Device with MAC address ${macAddress} already exists.`);
        return false;
      }

      const deviceRef = ref(db, `mdm/devices/${macAddress}`);
      await set(deviceRef, {
        macAddress,
        name: name || '',
        group: group || null,
        ping: Date.now(),
        system_version: null,
        app_list: [],
        ip_address: null,
        isRooted: null,
        servicePermissions: null,
      });
      return true;
    } catch (error) {
      toast.error(`Failed to add device ${macAddress}: ${error.message}`);
      return false;
    }
  };

  // Handle manual device addition
  const handleManualAdd = async () => {
    if (!macAddress) {
      toast.error('Please enter a MAC address.');
      return;
    }

    setIsAdding(true);
    const success = await addDevice({
      macAddress,
      name: deviceName,
      group: deviceGroup,
    });

    if (success) {
      toast.success(`Device ${macAddress} added successfully.`);
      setMacAddress('');
      setDeviceName('');
      setDeviceGroup('');
    }
    setIsAdding(false);
  };

  // Handle Excel file upload and parsing with batch writes
  const handleExcelUpload = async () => {
    if (!excelFile) {
      toast.error('Please select an Excel file to upload.');
      return;
    }

    setIsProcessingExcel(true);
    setProgress(0);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        // Validate column headers
        const requiredHeaders = ['MAC Address'];
        const headers = jsonData[0];
        const missingHeaders = requiredHeaders.filter((header) => !headers.includes(header));
        if (missingHeaders.length > 0) {
          toast.error(`Missing required columns in Excel file: ${missingHeaders.join(', ')}`);
          setIsProcessingExcel(false);
          return;
        }

        const devices = jsonData.slice(1).map((row) => {
          const rowData = {};
          headers.forEach((header, index) => {
            rowData[header] = row[index];
          });
          return {
            macAddress: rowData['MAC Address']?.toString().trim(),
            name: rowData['Name']?.toString().trim() || '',
            group: rowData['Group']?.toString().trim() || '',
          };
        });

        let successCount = 0;
        let errorCount = 0;
        const totalDevices = devices.length;
        const batchSize = 100;

        for (let i = 0; i < devices.length; i += batchSize) {
          const batch = devices.slice(i, i + batchSize);
          const updates = {};
          const batchErrors = [];

          for (const deviceData of batch) {
            if (!deviceData.macAddress) {
              batchErrors.push(`Missing MAC address in row: ${JSON.stringify(deviceData)}`);
              errorCount++;
              continue;
            }

            const formattedMac = formatMacAddress(deviceData.macAddress);
            if (!validateMacAddress(formattedMac)) {
              batchErrors.push(`Invalid MAC address format: ${deviceData.macAddress}. Expected format: XX:XX:XX:XX:XX:XX`);
              errorCount++;
              continue;
            }

            const exists = await checkDeviceExists(formattedMac);
            if (exists) {
              batchErrors.push(`Device with MAC address ${formattedMac} already exists.`);
              errorCount++;
              continue;
            }

            updates[`mdm/devices/${formattedMac}`] = {
              macAddress: formattedMac,
              name: deviceData.name || '',
              group: deviceData.group || null,
              ping: Date.now(),
              system_version: null,
              app_list: [],
              ip_address: null,
              isRooted: null,
              servicePermissions: null,
            };
            successCount++;
          }

          if (Object.keys(updates).length > 0) {
            try {
              await update(ref(db), updates);
            } catch (error) {
              toast.error(`Failed to add some devices in batch: ${error.message}`);
              successCount -= Object.keys(updates).length;
              errorCount += Object.keys(updates).length;
            }
          }

          batchErrors.forEach((error) => toast.error(error));

          const progressPercentage = Math.min(((i + batchSize) / totalDevices) * 100, 100);
          setProgress(progressPercentage);
        }

        toast.success(`Bulk import completed: ${successCount} devices added, ${errorCount} errors.`);
        setExcelFile(null);
      };
      reader.readAsArrayBuffer(excelFile);
    } catch (error) {
      toast.error(`Failed to process Excel file: ${error.message}`);
    } finally {
      setIsProcessingExcel(false);
      setProgress(0);
    }
  };

  // Download Excel template
  const downloadExcelTemplate = () => {
    const templateData = [
      { 'MAC Address': '00:11:22:33:44:55', 'Name': 'Device 1', 'Group': 'Group A' },
      { 'MAC Address': 'AA:BB:CC:DD:EE:FF', 'Name': 'Device 2', 'Group': 'Group B' },
    ];
    const worksheet = XLSX.utils.json_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Devices');
    XLSX.writeFile(workbook, 'device_import_template.xlsx');
  };

  return (
    <div className="p-6 bg-gray-900 text-white min-h-screen">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
      <h2 className="text-3xl font-bold mb-8 flex items-center gap-2">
        <FiMonitor className="text-indigo-400" /> Add New Device
      </h2>

      {/* Manual Input Section */}
      <div className="bg-gradient-to-br from-gray-800 to-gray-700 rounded-xl p-6 mb-6 shadow-lg">
        <h3 className="text-xl font-semibold mb-4">Add Device Manually</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-gray-300 mb-2">MAC Address</label>
            <input
              type="text"
              placeholder="e.g., 00:11:22:33:44:55"
              value={macAddress}
              onChange={handleMacAddressChange}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition"
              disabled={isAdding}
              maxLength={17} // 12 digits + 5 colons
            />
          </div>
          <div>
            <label className="block text-gray-300 mb-2">Device Name (Optional)</label>
            <input
              type="text"
              placeholder="e.g., Device 1"
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition"
              disabled={isAdding}
            />
          </div>
          <div>
            <label className="block text-gray-300 mb-2">Group (Optional)</label>
            <input
              type="text"
              placeholder="e.g., Group A"
              value={deviceGroup}
              onChange={(e) => setDeviceGroup(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition"
              disabled={isAdding}
            />
          </div>
          <button
            onClick={handleManualAdd}
            className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-4 py-2 rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all transform hover:scale-105 disabled:opacity-50 flex items-center"
            disabled={isAdding}
          >
            {isAdding ? (
              <>
                <FiLoader className="inline-block mr-2 animate-spin" /> Adding...
              </>
            ) : (
              <>
                <FiPlus className="inline-block mr-2" /> Add Device
              </>
            )}
          </button>
        </div>
      </div>

      {/* Bulk Import Section */}
      <div className="bg-gradient-to-br from-gray-800 to-gray-700 rounded-xl p-6 mb-6 shadow-lg">
        <h3 className="text-xl font-semibold mb-4">Bulk Import Devices via Excel</h3>
        <div className="space-y-4">
          <div>
            <label className="bg-gray-600 px-4 py-2 rounded-lg cursor-pointer hover:bg-gray-500 transition">
              <FiUpload className="inline-block mr-2" /> Upload Excel File
              <input
                type="file"
                accept=".xlsx, .xls"
                onChange={(e) => setExcelFile(e.target.files[0])}
                className="hidden"
                disabled={isProcessingExcel}
              />
            </label>
          </div>
          <button
            onClick={handleExcelUpload}
            className="bg-gradient-to-r from-green-500 to-teal-500 text-white px-4 py-2 rounded-lg hover:from-green-600 hover:to-teal-600 transition-all transform hover:scale-105 disabled:opacity-50 flex items-center"
            disabled={isProcessingExcel}
          >
            {isProcessingExcel ? (
              <>
                <FiLoader className="inline-block mr-2 animate-spin" /> Processing...
              </>
            ) : (
              <>
                <FiUpload className="inline-block mr-2" /> Import Devices
              </>
            )}
          </button>
          {isProcessingExcel && (
            <div className="w-full bg-gray-600 rounded-full h-2.5">
              <div
                className="bg-indigo-500 h-2.5 rounded-full"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          )}
          <button
            onClick={downloadExcelTemplate}
            className="bg-gradient-to-r from-gray-500 to-gray-600 text-white px-4 py-2 rounded-lg hover:from-gray-600 hover:to-gray-700 transition-all transform hover:scale-105"
          >
            <FiDownload className="inline-block mr-2" /> Download Template
          </button>
          <div className="mt-4">
            <h4 className="text-lg font-semibold text-gray-300">Excel File Format</h4>
            <p className="text-sm text-gray-400 mt-2">
              The Excel file must have the following columns:
            </p>
            <ul className="list-disc list-inside text-sm text-gray-400 mt-2">
              <li><strong>MAC Address</strong> (Required): The device MAC address in the format XX:XX:XX:XX:XX:XX (e.g., 00:11:22:33:44:55) or a 12-digit number (e.g., 001122334455).</li>
              <li><strong>Name</strong> (Optional): A name for the device (e.g., Device 1).</li>
              <li><strong>Group</strong> (Optional): The group to assign the device to (e.g., Group A).</li>
            </ul>
            <p className="text-sm text-gray-400 mt-2">
              Download the template above for an example. Ensure the column names match exactly as specified.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}